import React, { useState, useEffect } from 'react';
import { X, User, MapPin, Calendar, Clock, Save, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import styles from './Overtime.module.css';
import CustomSelect from '../../../components/Common/Select/CustomSelect';
import CustomDatePicker from '../../../components/Common/DatePicker/CustomDatePicker';
import toast from 'react-hot-toast';
import api from '../../../api/axios';

const OTAttendanceDrawer = ({ isOpen, onClose, onSave, record = null }) => {
  const [formData, setFormData] = useState({
    employee_id: '',
    project_site_id: '',
    date: '',
    ot_punch_in: '',
    ot_punch_out: '',
    ot_hours: '',
    status: 'pending'
  });

  const [loading, setLoading] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [sites, setSites] = useState([]);

  // Fetch dropdown choices dynamically
  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const [empRes, projRes] = await Promise.all([
          api.get('/employees-dropdown'),
          api.get('/projects-dropdown')
        ]);

        if (empRes.data && empRes.data.data) {
          setEmployees(empRes.data.data.map(emp => ({
            value: emp.id,
            label: emp.employee_name
          })));
        } else if (empRes.data) {
          setEmployees(empRes.data.map(emp => ({
            value: emp.id,
            label: emp.employee_name
          })));
        }

        if (projRes.data && projRes.data.data) {
          setSites(projRes.data.data.map(proj => ({
            value: proj.id,
            label: proj.project_name
          })));
        } else if (projRes.data) {
          setSites(projRes.data.map(proj => ({
            value: proj.id,
            label: proj.project_name
          })));
        }
      } catch (error) {
        console.error('Error fetching dropdown choices in OTAttendanceDrawer:', error);
      }
    };

    if (isOpen) {
      fetchOptions();
    }
  }, [isOpen]);

  const statusOptions = [
    { value: 'pending', label: 'Pending' },
    { value: 'approved', label: 'Approved' },
    { value: 'rejected', label: 'Rejected' }
  ];

  useEffect(() => {
    if (record && isOpen) {
      setFormData({
        employee_id: record.employee_id || '',
        project_site_id: record.project_site_id || '',
        date: record.date || '',
        ot_punch_in: record.ot_punch_in || '',
        ot_punch_out: record.ot_punch_out || '',
        ot_hours: record.ot_hours || '',
        status: record.status || 'pending'
      });
    } else if (!record) {
      setFormData({
        employee_id: '',
        project_site_id: '',
        date: '',
        ot_punch_in: '',
        ot_punch_out: '',
        ot_hours: '',
        status: 'pending'
      });
    }
  }, [isOpen, record]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.employee_id) {
      toast.error('Please select an employee');
      return;
    }
    if (!formData.project_site_id) {
      toast.error('Please select a project site');
      return;
    }
    if (!formData.date) {
      toast.error('Please select a date');
      return;
    }

    setLoading(true);
    try {
      if (record && record.id) {
        // If editing a real record with ID, execute the edit-timings API call
        const payload = {
          punch_in_at: `${formData.date} ${formData.ot_punch_in || '18:00'}`,
          punch_out_at: `${formData.date} ${formData.ot_punch_out || '22:00'}`,
          remarks: 'Updated via Admin Panel'
        };
        await api.put(`/overtime/assignments/${record.id}/edit-timings`, payload);
      }

      toast.success(record ? 'OT Log updated' : 'OT Hours logged successfully');
      
      const selectedEmp = employees.find(e => e.value === formData.employee_id);
      const selectedSite = sites.find(s => s.value === formData.project_site_id);
      
      if (onSave) {
        onSave({
          ...formData,
          id: record ? record.id : Date.now(),
          employee: selectedEmp ? selectedEmp.label : 'Employee',
          project_site: selectedSite ? selectedSite.label : 'Project Site'
        });
      }
      onClose();
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.message || 'Failed to save OT log timings');
    } finally {
      setLoading(false);
    }
  };


  if (!isOpen) return null;

  return (
    <div className={styles['drawer-backdrop']} onClick={onClose}>
      <div className={styles['drawer-container']} onClick={e => e.stopPropagation()}>
        <div className={styles['drawer-header']}>
          <div className={styles['header-title-wrapper']}>
            <h2>{record ? 'Edit OT Log' : 'Log OT Attendance'}</h2>
            <p>Record actual overtime hours for verification.</p>
          </div>
          <button className={styles['close-btn']} onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className={styles['drawer-form']}>
          <div className={styles['form-scroll-area']}>
            <div className={styles['form-group']}>
              <label><User size={16} /> Employee</label>
              <CustomSelect 
                options={employees}
                value={employees.find(e => e.value === formData.employee_id)}
                onChange={(opt) => setFormData({...formData, employee_id: opt.value})}
                placeholder="Select Employee"
              />
            </div>

            <div className={styles['form-group']}>
              <label><MapPin size={16} /> Project Site</label>
              <CustomSelect 
                options={sites}
                value={sites.find(s => s.value === formData.project_site_id)}
                onChange={(opt) => setFormData({...formData, project_site_id: opt.value})}
                placeholder="Select Site"
              />
            </div>

            <div className={styles['form-group']}>
              <label><Calendar size={16} /> Date</label>
              <CustomDatePicker 
                selected={formData.date}
                onChange={(date) => setFormData({...formData, date: date.toLocaleDateString('en-CA')})}
              />
            </div>

            <div className={styles['form-row']}>
              <div className={styles['form-group']}>
                <label><Clock size={16} /> OT In</label>
                <input 
                  type="time" 
                  value={formData.ot_punch_in}
                  onChange={(e) => setFormData({...formData, ot_punch_in: e.target.value})}
                  className={styles['form-input']}
                />
              </div>
              <div className={styles['form-group']}>
                <label><Clock size={16} /> OT Out</label>
                <input 
                  type="time" 
                  value={formData.ot_punch_out}
                  onChange={(e) => setFormData({...formData, ot_punch_out: e.target.value})}
                  className={styles['form-input']}
                />
              </div>
            </div>

            <div className={styles['form-group']}>
              <label><Clock size={16} /> Total OT Hours</label>
              <input 
                type="number" 
                step="0.1"
                placeholder="e.g. 3.5"
                value={formData.ot_hours}
                onChange={(e) => setFormData({...formData, ot_hours: e.target.value})}
                className={styles['form-input']}
              />
            </div>

            <div className={styles['form-group']}>
              <label><AlertCircle size={16} /> Status</label>
              <CustomSelect 
                options={statusOptions}
                value={statusOptions.find(s => s.value === formData.status)}
                onChange={(opt) => setFormData({...formData, status: opt.value})}
              />
            </div>
          </div>

          <div className={styles['drawer-footer']}>
            <button type="button" className={styles['cancel-btn']} onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className={styles['save-btn']} disabled={loading}>
              {loading ? <Loader2 size={18} className={styles['spinner']} /> : <Save size={18} />}
              <span>{record ? 'Update Log' : 'Save Log'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default OTAttendanceDrawer;
